import { shallowRef } from 'vue';

export type MobileDebugLevel = 'log' | 'info' | 'warn' | 'error';

export interface MobileDebugEntry {
  id: number;
  level: MobileDebugLevel;
  message: string;
  timestamp: number;
}

const MAX_ENTRIES = 300;
const MAX_MESSAGE_LENGTH = 4000;

export const mobileDebugEntries = shallowRef<MobileDebugEntry[]>([]);

const nativeConsole = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console)
};

let nextEntryId = 1;
let installed = false;

/** 默认关闭；仅在 URL 显式携带 ?debug=1 时启用移动端诊断控制台。 */
export const isMobileDebugConsoleEnabled = () =>
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

const stringifyValue = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.stack || `${value.name}: ${value.message}`;
  if (value instanceof Event) return `[${value.constructor.name} type=${value.type}]`;

  const seen = new WeakSet<object>();

  try {
    const result = JSON.stringify(value, (_key, nestedValue: unknown) => {
      if (typeof nestedValue === 'bigint') return `${nestedValue}n`;
      if (typeof nestedValue === 'function') {
        return `[Function ${nestedValue.name || 'anonymous'}]`;
      }
      if (typeof nestedValue === 'symbol') return String(nestedValue);
      if (nestedValue instanceof Error) {
        return nestedValue.stack || `${nestedValue.name}: ${nestedValue.message}`;
      }
      if (nestedValue instanceof Node) {
        return `<${nestedValue.nodeName.toLowerCase()}>`;
      }
      if (typeof nestedValue === 'object' && nestedValue !== null) {
        if (seen.has(nestedValue)) return '[Circular]';
        seen.add(nestedValue);
      }
      return nestedValue;
    });

    return result ?? String(value);
  } catch {
    try {
      return String(value);
    } catch {
      return '[Unserializable value]';
    }
  }
};

const appendEntry = (level: MobileDebugLevel, values: unknown[]) => {
  const rawMessage = values.map(stringifyValue).join(' ');
  const message =
    rawMessage.length > MAX_MESSAGE_LENGTH
      ? `${rawMessage.slice(0, MAX_MESSAGE_LENGTH)}…[truncated]`
      : rawMessage;

  const nextEntries = [
    ...mobileDebugEntries.value,
    {
      id: nextEntryId++,
      level,
      message,
      timestamp: Date.now()
    }
  ];

  mobileDebugEntries.value =
    nextEntries.length > MAX_ENTRIES ? nextEntries.slice(-MAX_ENTRIES) : nextEntries;
};

const getResourceUrl = (target: EventTarget | null): string | null => {
  if (!(target instanceof HTMLElement)) return null;

  const resourceTarget = target as HTMLElement & {
    currentSrc?: string;
    src?: string;
    href?: string;
  };
  return resourceTarget.currentSrc || resourceTarget.src || resourceTarget.href || null;
};

export const installMobileDebugConsole = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  (Object.keys(nativeConsole) as MobileDebugLevel[]).forEach((level) => {
    console[level] = (...values: unknown[]) => {
      nativeConsole[level](...values);
      appendEntry(level, values);
    };
  });

  window.addEventListener(
    'error',
    (event: Event) => {
      const resourceUrl = getResourceUrl(event.target);
      if (resourceUrl) {
        appendEntry('error', [`[resource:error] ${resourceUrl}`]);
        return;
      }

      if (event instanceof ErrorEvent) {
        appendEntry('error', [
          '[window:error]',
          event.message,
          `${event.filename || 'unknown'}:${event.lineno}:${event.colno}`,
          event.error
        ]);
      }
    },
    true
  );

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    appendEntry('error', ['[promise:unhandledrejection]', event.reason]);
  });

  appendEntry('info', [
    '[environment]',
    {
      href: window.location.href,
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      devicePixelRatio: window.devicePixelRatio,
      online: navigator.onLine
    }
  ]);
};

export const debugCheckpoint = (scope: string, message: string, data?: unknown) => {
  if (!installed) return;

  const values: unknown[] = [`[checkpoint:${scope}] ${message}`];
  if (data !== undefined) values.push(data);

  console.info(...values);
};

export const clearMobileDebugEntries = () => {
  mobileDebugEntries.value = [];
};

export const formatMobileDebugEntries = () =>
  mobileDebugEntries.value
    .map((entry) => {
      const time = new Date(entry.timestamp).toISOString();
      return `${time} [${entry.level.toUpperCase()}] ${entry.message}`;
    })
    .join('\n');
